import { toast } from 'react-toastify'
import { LaravelError, ExpectedError } from './laravel_errors.utils'
import { AxiosError } from 'axios'

/**
 * Handles a service action/promise with standardized toast notifications and error handling.
 *
 * @param {Promise} actionPromise - The promise to handle
 * @param {Object} options - Options object
 * @param {string} options.pending - Message for pending state
 * @param {string} options.success - Message for success state
 * @param {Function} options.t - Translation function (useT hook result)
 * @param {Function} [options.onSuccess] - Callback on success
 * @param {Function} [options.onError] - Callback on error
 */
export const handleServiceAction = async (
  actionPromise,
  { pending = 'Loading...', success = 'Success', t = (s) => s, onSuccess, onError } = {},
) => {
  // Wrap the promise to strictly enforce 200/201 status as success
  const wrappedPromise = actionPromise.then((response) => {
    // Check for 308 explicitly as an expected error
    if (response?.status === 308) {
      throw new ExpectedError(
        response.data?.message || 'Error 308',
        308,
        'expected_error',
        true, // alreadyTranslatedMessage matches the user's "message to show user"
      )
    }

    // If it's a valid response object with a status
    if (response && response.status && ![200, 201].includes(response.status)) {
      // Create an error that looks like an AxiosError so our render logic works
      const error = new AxiosError(
        'Request failed with status code ' + response.status,
        response.status.toString(),
        undefined,
        undefined,
        response,
      )
      throw error // This triggers the error state in toast.promise
    }
    return response
  })

  const toastPromise = toast.promise(wrappedPromise, {
    pending,
    success,
    error: {
      render({ data: error }) {
        if (error instanceof LaravelError) {
          const errorKeys = Object.keys(error.errors)
          if (errorKeys.length > 0) {
            return error.errors[errorKeys[0]][0] // Show first error of first field
          }
        }
        if (error instanceof ExpectedError) {
          return error.alreadyTranslatedMessage ? error.message : t(error.message)
        }
        if (error instanceof AxiosError) {
          if (error.response?.status === 403) return t('no_permissions_to') ?? 'No permissions'
          if (error.response?.status === 429) return t('too_many_requests') ?? 'Too many requests'
          // Handle 308 specifically as requested, or any other status with a message
          if (error.response?.status === 308 && error.response?.data?.message) {
            return error.response.data.message
          }
          // Fallback: If server sent a message, use it
          if (error.response?.data?.message) {
            return error.response.data.message
          }
        }
        return t('unknown_error') || 'Unknown error'
      },
    },
  })

  try {
    const result = await toastPromise
    if (onSuccess) onSuccess(result)
    return result
  } catch (error) {
    if (onError) onError(error)
    return null // Swallow error to prevent crash
  }
}
